import { FormEvent, useEffect, useMemo, useState } from "react";
import { Calendar, CheckCircle2, ChevronDown, Clipboard, FileText, Loader2, Pencil, Terminal, Wand2 } from "lucide-react";
import { AgentCommandResult, AgentProvider, api, Task } from "../lib/tauri";
import { buildExecutionCard, buildExecutionCardPreview, cleanAgentOutput, shouldRepairStoredMission, type MissionGoAction } from "../lib/missionExecutionCard";
import { useTaskStore } from "../stores/taskStore";
import { Button } from "./ui/Button";
import { Input } from "./ui/Input";
import { RadioGroup } from "./ui/RadioGroup";
import { Textarea } from "./ui/Textarea";
import { useUiStore } from "../stores/uiStore";
import { todayIso } from "../lib/laneCore";
import { providerLabel } from "../lib/ai-pwa";

function sectionValue(text: string, label: string): string {
  const pattern = new RegExp(`${label}:\\n([\\s\\S]*?)(?=\\n\\n[A-Za-z一-龠ぁ-んァ-ンー/]+:|$)`);
  return text.match(pattern)?.[1]?.trim() ?? "";
}

function codeBlockValue(text: string): string {
  return text.match(/```([\s\S]*?)```/)?.[1]?.trim() ?? "";
}

function listSection(text: string, label: string): string[] {
  return sectionValue(text, label)
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

interface AppendedAgentLog {
  header: string;
  body: string;
}

interface AgentResultDigest {
  recommendedAction: MissionGoAction | null;
  nextActions: string[];
  safetyTags: string[];
}

function appendedAgentLogs(text: string): AppendedAgentLog[] {
  const marker = /(?:^|\n)---\nAgent Result追記 \(([^)]+)\)\n\n/g;
  const matches = Array.from(text.matchAll(marker));

  return matches
    .map((match, index) => {
      const bodyStart = (match.index ?? 0) + match[0].length;
      const nextStart = matches[index + 1]?.index ?? text.length;

      return {
        header: match[1]?.trim() || `追記 ${index + 1}`,
        body: text.slice(bodyStart, nextStart).trim(),
      };
    })
    .filter((log) => log.body);
}

function extractRecommendedAction(text: string): MissionGoAction | null {
  const normalized = text.replace(/\s+/g, " ");
  const notThisBut = normalized.match(/[A-D]ではなく、?まず`?\s*([A-D])\s*`?/);
  if (notThisBut?.[1]) return notThisBut[1] as MissionGoAction;

  const patterns = [
    /(?:おすすめ|推奨)(?:判断)?(?:は|:|：)?\s*`?\s*([A-D])\s*`?(?:\s|。|、|です|のまま|継続|$)/,
    /(?:ネクストアクション|次の1手)[\s\S]*?推奨[:：]?\s*`?\s*([A-D])\s*`?/,
    /`?\s*([A-D])\s*`?\s*で(?:、|「|現状|対象|TimereX|診断|focus|diff|送信なし)/,
  ];

  for (const pattern of patterns) {
    const match = normalized.match(pattern);
    if (match?.[1]) return match[1] as MissionGoAction;
  }

  return null;
}

function extractNumberedActions(text: string): string[] {
  const lines = text.split("\n").map((line) => line.trim());
  const headingIndex = lines.findIndex((line) => /^(ネクストアクション|次の1手|次にやること)[:：]?$/.test(line));
  const sourceLines = headingIndex >= 0 ? lines.slice(headingIndex + 1) : lines;

  return sourceLines
    .map((line) => line.match(/^\d+[.．]\s*(.+)$/)?.[1]?.trim())
    .filter((line): line is string => Boolean(line))
    .slice(0, 3);
}

function extractSafetyTags(text: string): string[] {
  const checks: Array<[RegExp, string]> = [
    [/本番送信なし|本番送信は別|実送信は別/, "本番送信なし"],
    [/外部投稿なし|外部書き込みなし|外部更新なし/, "外部書き込みなし"],
    [/git pushなし|pushなし/, "git pushなし"],
    [/deployなし|デプロイなし/, "deployなし"],
    [/認証情報.*出さない|認証情報.*表示しない|token.*出力されない/, "認証情報なし"],
  ];

  return checks.filter(([pattern]) => pattern.test(text)).map(([, label]) => label);
}

function buildAgentResultDigest(output: string): AgentResultDigest {
  const cleaned = cleanAgentOutput(output);
  return {
    recommendedAction: extractRecommendedAction(cleaned),
    nextActions: extractNumberedActions(cleaned),
    safetyTags: extractSafetyTags(cleaned),
  };
}

function hasDigestContent(digest: AgentResultDigest): boolean {
  return Boolean(digest.recommendedAction || digest.nextActions.length || digest.safetyTags.length);
}

function AgentDigestCard({ digest }: { digest: AgentResultDigest }) {
  if (!hasDigestContent(digest)) return null;

  return (
    <div className="rounded-md border border-brand-200 bg-brand-50 px-3 py-3 dark:border-brand-500/25 dark:bg-brand-500/10">
      <div className="grid gap-3 md:grid-cols-[180px_1fr]">
        <div>
          <p className="text-xs text-muted dark:text-muted-dark">おすすめGO</p>
          <p className="mt-1 text-lg font-semibold">
            {digest.recommendedAction
              ? `${digest.recommendedAction} / ${decisionActionMeta[digest.recommendedAction].label}`
              : "未抽出"}
          </p>
        </div>
        {digest.nextActions.length > 0 && (
          <div>
            <p className="text-xs text-muted dark:text-muted-dark">次の一手</p>
            <div className="mt-1 space-y-1">
              {digest.nextActions.map((item, index) => (
                <p key={`${item}-${index}`} className="text-sm">
                  {index + 1}. {item}
                </p>
              ))}
            </div>
          </div>
        )}
      </div>
      {digest.safetyTags.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {digest.safetyTags.map((tag) => (
            <span
              key={tag}
              className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-100"
            >
              {tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

const agentLabel: Record<AgentProvider, string> = {
  codex: providerLabel("codex"),
  claude: providerLabel("claude"),
};

const decisionActionMeta: Record<MissionGoAction, { label: string; description: string; tone: string }> = {
  A: {
    label: "調査",
    description: "状況確認だけ",
    tone: "border-sky-200 bg-sky-50 text-sky-950 hover:bg-sky-100 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-100",
  },
  B: {
    label: "案作成",
    description: "diff案だけ",
    tone: "border-indigo-200 bg-indigo-50 text-indigo-950 hover:bg-indigo-100 dark:border-indigo-500/30 dark:bg-indigo-500/10 dark:text-indigo-100",
  },
  C: {
    label: "実行準備",
    description: "C前再ゲート",
    tone: "border-emerald-200 bg-emerald-50 text-emerald-950 hover:bg-emerald-100 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-100",
  },
  D: {
    label: "保留",
    description: "今日は止める",
    tone: "border-border bg-bg text-text hover:bg-muted/10 dark:border-border-dark dark:bg-bg-dark dark:text-text-dark",
  },
};

function actionFromGo(go: string): string | null {
  return go.match(/GO\s*([A-D])/)?.[1] ?? null;
}

function buildContinuationPrompt(
  action: MissionGoAction,
  missionTitle: string,
  latestLog: string,
  userNote: string
): string {
  const meta = decisionActionMeta[action];
  const trimmedNote = userNote.trim();

  return `Flow Command Centerの最新ログを踏まえて、次のGOで続投してください。

Mission:
${missionTitle}

GO:
GO ${action} / ${meta.label}

制約:
- 本番送信なし
- 外部投稿なし
- git pushなし
- deployなし
- 認証情報を表示しない
- 危険作業はA/B/C/Dで確認
- Cの場合も、まずC前再ゲートとしてscope・rollback・確認方法・停止条件を出す

期待する出力:
1. GO ${action}で進める具体手順
2. AIでできる範囲
3. たいがさんが確認する範囲
4. 次のA/B/C/D判断

たいがさんの追加入力:
${trimmedNote || "なし"}

最新ログ:
${latestLog}`;
}

function withAdditionalConsultation(prompt: string, userNote: string): string {
  const trimmedNote = userNote.trim();
  if (!trimmedNote) return prompt;

  return `${prompt.trimEnd()}

---
たいがさんの追加入力:
${trimmedNote}`;
}

export function TaskForm({ task, onClose }: { task?: Task; onClose: () => void }) {
  const createTask = useTaskStore((s) => s.createTask);
  const updateTask = useTaskStore((s) => s.updateTask);
  const toggleTask = useTaskStore((s) => s.toggleTask);
  const commandMode = useUiStore((s) => s.appView === "command");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<"high" | "medium" | "low">("medium");
  const [dueDate, setDueDate] = useState("");
  const [autoRepaired, setAutoRepaired] = useState(false);
  const [descriptionMode, setDescriptionMode] = useState<"view" | "edit">("edit");
  const [runningAgent, setRunningAgent] = useState<AgentProvider | null>(null);
  const [agentResult, setAgentResult] = useState<AgentCommandResult | null>(null);
  const [agentError, setAgentError] = useState<string | null>(null);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [appendState, setAppendState] = useState<"idle" | "saved" | "error">("idle");
  const [appendError, setAppendError] = useState<string | null>(null);
  const [selectedDecisionAction, setSelectedDecisionAction] = useState<MissionGoAction | null>(null);
  const [followupDraft, setFollowupDraft] = useState("");
  const isEdit = Boolean(task);

  useEffect(() => {
    const nextTitle = task?.title ?? "";
    const nextDescription = task?.description ?? "";
    const shouldRepairMission =
      Boolean(task) &&
      shouldRepairStoredMission(nextTitle, nextDescription);

    if (shouldRepairMission) {
      const preview = buildExecutionCardPreview(nextDescription, null);
      setTitle(preview.title);
      setDescription(buildExecutionCard(nextDescription, preview.goAction));
      setAutoRepaired(true);
      setDescriptionMode("view");
    } else {
      setTitle(nextTitle);
      setDescription(nextDescription);
      setAutoRepaired(false);
      setDescriptionMode(/^実行カード/.test(nextDescription) ? "view" : "edit");
    }
    setPriority(task?.priority ?? "medium");
    setDueDate(task?.dueDate ?? "");
    setRunningAgent(null);
    setAgentResult(null);
    setAgentError(null);
    setShowDiagnostics(false);
    setAppendState("idle");
    setAppendError(null);
    setSelectedDecisionAction(null);
    setFollowupDraft("");
  }, [task]);

  const quickDates = useMemo(() => {
    const today = new Date();
    const fmt = todayIso;
    const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
    const next = new Date(today); next.setDate(today.getDate() + 7);
    return [{ label: "今日", value: fmt(today) }, { label: "明日", value: fmt(tomorrow) }, { label: "来週", value: fmt(next) }];
  }, []);

  const submit = async (keep = false) => {
    if (!title.trim()) return;
    if (isEdit && task) {
      await updateTask(task.id, { title, description, priority, dueDate: dueDate || null, clearDueDate: !dueDate });
      onClose();
    } else {
      await createTask({ title, description, priority, dueDate: dueDate || null });
      if (keep) {
        setTitle("");
        setDescription("");
        setDueDate("");
        setPriority("medium");
      } else {
        onClose();
      }
    }
  };

  const closeMission = async (mode: "complete" | "tomorrow" | "hold") => {
    if (!isEdit || !task || !title.trim()) return;
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowValue = todayIso(tomorrow);
    const basePatch = {
      title,
      description,
      priority,
      dueDate: dueDate || null,
      clearDueDate: !dueDate,
    };

    if (mode === "complete") {
      await updateTask(task.id, basePatch);
      if (!task.completed) {
        await toggleTask(task.id);
      }
      onClose();
      return;
    }

    if (mode === "tomorrow") {
      await updateTask(task.id, {
        ...basePatch,
        completed: false,
        dueDate: tomorrowValue,
        clearDueDate: false,
      });
      onClose();
      return;
    }

    await updateTask(task.id, {
      ...basePatch,
      completed: false,
      priority: "low",
      dueDate: null,
      clearDueDate: true,
    });
    onClose();
  };

  const isExecutionCard = /^実行カード/.test(description);
  const executionPurpose = sectionValue(description, "目的");
  const executionGo = sectionValue(description, "GO");
  const executionMode = sectionValue(description, "実行判断");
  const executionAgent = sectionValue(description, "推奨実行面");
  const executionNextActions = listSection(description, "次の行動");
  const executionPrompt = codeBlockValue(description);
  const executionWatchPoints = listSection(description, "たいがさんが見るポイント");
  const executionAppendedLogs = appendedAgentLogs(description);
  const latestAppendedLog = executionAppendedLogs[executionAppendedLogs.length - 1] ?? null;
  const previousAppendedLogs = executionAppendedLogs.slice(0, -1);
  const latestLogDigest = latestAppendedLog ? buildAgentResultDigest(latestAppendedLog.body) : null;
  const liveAgentDigest = agentResult ? buildAgentResultDigest(agentResult.output) : null;
  const compactMissionFields = isExecutionCard && descriptionMode === "view" && executionAppendedLogs.length > 0;
  const activeDecisionAction = selectedDecisionAction ?? latestLogDigest?.recommendedAction ?? null;
  const continuationPrompt =
    activeDecisionAction && latestAppendedLog
      ? buildContinuationPrompt(
          activeDecisionAction,
          title || executionPurpose || "Mission未設定",
          latestAppendedLog.body,
          followupDraft
        )
      : "";
  const displayNextActions =
    executionAppendedLogs.length > 0
      ? [
          "最新の追記ログを確認する。",
          "diff案または確認結果を見て、Command CenterでA/B/C/Dを再判断する。",
          "追加相談が必要な時だけ、Codex/Claudeへ再投げする。",
        ]
      : executionNextActions;

  const copyDescription = async () => {
    await navigator.clipboard?.writeText(description);
  };

  const runExecutionAgent = async (
    provider: AgentProvider,
    promptOverride?: string,
    actionOverride?: MissionGoAction | string | null
  ) => {
    const promptToRun = promptOverride ?? executionPrompt;
    if (!promptToRun.trim()) return;
    setRunningAgent(provider);
    setAgentResult(null);
    setAgentError(null);
    setShowDiagnostics(false);
    setAppendState("idle");
    setAppendError(null);
    try {
      if (!api.runAgentCommand) {
        throw new Error("CLI agent bridge is available only in the Tauri Desktop app.");
      }
      const result = await api.runAgentCommand({
        provider,
        prompt: promptToRun,
        action: actionOverride ?? actionFromGo(executionGo),
        missionTitle: title || executionPurpose || null,
      });
      setAgentResult(result);
    } catch (error) {
      setAgentError(error instanceof Error ? error.message : String(error));
    } finally {
      setRunningAgent(null);
    }
  };

  const appendAgentResultToMission = async () => {
    if (!agentResult) return;
    const cleanedOutput = cleanAgentOutput(agentResult.output);
    if (!cleanedOutput) return;
    const stamp = new Date().toLocaleString("ja-JP", {
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
    const addition = [
      "",
      "",
      "---",
      `Agent Result追記 (${agentLabel[agentResult.provider]} / ${stamp})`,
      "",
      cleanedOutput,
    ].join("\n");
    const nextDescription = `${description.trimEnd()}${addition}`;

    setAppendState("idle");
    setAppendError(null);
    setDescription(nextDescription);

    try {
      if (isEdit && task) {
        await updateTask(task.id, {
          title,
          description: nextDescription,
          priority,
          dueDate: dueDate || null,
          clearDueDate: !dueDate,
        });
      }
      setAppendState("saved");
      setAgentResult(null);
      setAgentError(null);
      setShowDiagnostics(false);
      setSelectedDecisionAction(null);
      setFollowupDraft("");
      setDescriptionMode("view");
    } catch (error) {
      setAppendState("error");
      setAppendError(error instanceof Error ? error.message : String(error));
    }
  };

  return (
    <form
      onSubmit={(e: FormEvent) => { e.preventDefault(); void submit(false); }}
      onKeyDown={(e) => {
        if (e.key === "Enter" && e.metaKey) {
          e.preventDefault();
          void submit(e.shiftKey);
        }
      }}
    >
      <div className="border-b border-border px-5 py-4 dark:border-border-dark">
        <h2 className="text-lg font-semibold">
          {commandMode ? (isEdit ? "Mission編集" : "Mission登録") : isEdit ? "タスク編集" : "新規タスク"}
        </h2>
      </div>
      <div className="flow-scrollbar max-h-[calc(100vh-180px)] space-y-4 overflow-y-auto p-5 md:max-h-[72vh]">
        {autoRepaired && (
          <div className="rounded-md border border-violet-200 bg-violet-50 px-3 py-2 text-sm leading-relaxed text-violet-900 dark:border-violet-500/30 dark:bg-violet-500/10 dark:text-violet-100">
            <div className="flex items-start gap-2">
              <Wand2 className="mt-0.5 h-4 w-4 shrink-0" />
              <p>
                壊れたMission名を元のAgent Resultから復元しました。内容を確認して保存すると、このMissionが修正されます。
              </p>
            </div>
          </div>
        )}
        <Input autoFocus placeholder={commandMode ? "Mission名" : "タイトル"} value={title} onChange={(e) => setTitle(e.target.value)} />
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-xs font-medium text-muted dark:text-muted-dark">
              <FileText className="h-3.5 w-3.5" />
              本文
            </div>
            <div className="grid grid-cols-2 gap-2 sm:flex">
              {isExecutionCard && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  leftIcon={<Pencil className="h-3.5 w-3.5" />}
                  onClick={() => setDescriptionMode(descriptionMode === "view" ? "edit" : "view")}
                >
                  {descriptionMode === "view" ? "本文編集" : "カード表示"}
                </Button>
              )}
              <Button
                type="button"
                size="sm"
                variant="outline"
                leftIcon={<Clipboard className="h-3.5 w-3.5" />}
                onClick={() => void copyDescription()}
              >
                コピー
              </Button>
            </div>
          </div>

          {isExecutionCard && descriptionMode === "view" ? (
            <div className="space-y-3 rounded-md border border-border bg-card px-4 py-4 text-sm leading-relaxed dark:border-border-dark dark:bg-card-dark">
              <div className="grid gap-3 md:grid-cols-3">
                <div className="rounded-md border border-border bg-bg px-3 py-3 dark:border-border-dark dark:bg-bg-dark">
                  <p className="text-xs text-muted dark:text-muted-dark">目的</p>
                  <p className="mt-1 font-semibold">{executionPurpose || title}</p>
                </div>
                <div className="rounded-md border border-border bg-bg px-3 py-3 dark:border-border-dark dark:bg-bg-dark">
                  <p className="text-xs text-muted dark:text-muted-dark">GO</p>
                  <p className="mt-1 font-semibold">{executionGo || "未設定"}</p>
                </div>
                <div className="rounded-md border border-border bg-bg px-3 py-3 dark:border-border-dark dark:bg-bg-dark">
                  <p className="text-xs text-muted dark:text-muted-dark">推奨実行面</p>
                  <p className="mt-1 font-semibold">{executionAgent || "未設定"}</p>
                </div>
              </div>

              {executionMode && (
                <div className="rounded-md border border-brand-200 bg-brand-50 px-3 py-3 text-brand-950 dark:border-brand-500/25 dark:bg-brand-500/10 dark:text-brand-100">
                  {executionMode}
                </div>
              )}

              {executionAppendedLogs.length > 0 && (
                <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-3 text-emerald-950 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-100">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                  <p>AI結果は本文に追記済みです。次は最新ログを見て、A/B/C/Dを再判断します。</p>
                </div>
              )}

              {displayNextActions.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted dark:text-muted-dark">次の行動</p>
                  <div className="mt-2 space-y-2">
                    {displayNextActions.map((item, index) => (
                      <div key={`${item}-${index}`} className="flex gap-2 rounded-md border border-border bg-bg px-3 py-2 dark:border-border-dark dark:bg-bg-dark">
                        <span className="font-semibold text-brand-600 dark:text-brand-300">{index + 1}</span>
                        <span>{item.replace(/^\d+[.．]\s*/, "")}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {latestAppendedLog && (
                <div>
                  <div className="flex flex-col gap-1 md:flex-row md:items-end md:justify-between">
                    <p className="text-xs font-medium text-muted dark:text-muted-dark">最新の追記ログ</p>
                    <p className="text-xs text-muted dark:text-muted-dark">Agent Result追記 ({latestAppendedLog.header})</p>
                  </div>
                  {latestLogDigest && (
                    <div className="mt-2">
                      <AgentDigestCard digest={latestLogDigest} />
                    </div>
                  )}
                  <pre className="flow-scrollbar mt-2 max-h-96 overflow-y-auto whitespace-pre-wrap break-words rounded-md border border-emerald-200 bg-emerald-50 px-3 py-3 font-sans text-sm leading-relaxed text-emerald-950 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-100">
                    {latestAppendedLog.body}
                  </pre>
                </div>
              )}

              {latestAppendedLog && (
                <div className="rounded-md border border-border bg-bg px-3 py-3 dark:border-border-dark dark:bg-bg-dark">
                  <p className="text-xs font-medium text-muted dark:text-muted-dark">次の判断</p>
                  <div className="mt-2 grid grid-cols-2 gap-2 md:grid-cols-4">
                    {(Object.keys(decisionActionMeta) as MissionGoAction[]).map((action) => {
                      const meta = decisionActionMeta[action];
                      const selected = activeDecisionAction === action;
                      return (
                        <button
                          key={action}
                          type="button"
                          className={`rounded-md border px-3 py-3 text-left transition ${meta.tone} ${selected ? "ring-2 ring-brand-500 dark:ring-brand-300" : ""}`}
                          onClick={() => setSelectedDecisionAction(action)}
                        >
                          <span className="block font-mono text-lg font-bold">{action}</span>
                          <span className="block text-sm font-semibold">{meta.label}</span>
                          <span className="mt-0.5 block text-xs opacity-75">{meta.description}</span>
                        </button>
                      );
                    })}
                  </div>
                  {activeDecisionAction && (
                    <div className="mt-3 rounded-md border border-brand-200 bg-brand-50 px-3 py-3 dark:border-brand-500/25 dark:bg-brand-500/10">
                      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div>
                          <p className="text-xs text-muted dark:text-muted-dark">
                            {selectedDecisionAction ? "選択中" : "おすすめを自動選択"}
                          </p>
                          <p className="font-semibold">
                            GO {activeDecisionAction} / {decisionActionMeta[activeDecisionAction].label}
                          </p>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {(["codex", "claude"] as AgentProvider[]).map((provider) => (
                            <Button
                              key={provider}
                              type="button"
                              size="sm"
                              variant={provider === "codex" ? "primary" : "outline"}
                              leftIcon={
                                runningAgent === provider ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <Terminal className="h-3.5 w-3.5" />
                                )
                              }
                              disabled={runningAgent !== null}
                              onClick={() => void runExecutionAgent(provider, continuationPrompt, activeDecisionAction)}
                            >
                              {agentLabel[provider]}へ続投
                            </Button>
                          ))}
                        </div>
                      </div>
                      <div className="mt-3">
                        <p className="text-xs font-medium text-muted dark:text-muted-dark">たいがさんの追加入力</p>
                        <Textarea
                          value={followupDraft}
                          onChange={(e) => setFollowupDraft(e.target.value)}
                          placeholder="例: B寄りだけど、診断LP経由とTimereX直リンクの両方で文面案を出して。本番送信は絶対しない。"
                          className="mt-2 min-h-28 resize-y bg-white text-sm leading-relaxed dark:bg-bg-dark"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {executionPrompt && executionAppendedLogs.length === 0 && (
                <div>
                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <p className="text-xs font-medium text-muted dark:text-muted-dark">AIへ投げるプロンプト</p>
                    <div className="grid grid-cols-2 gap-2">
                      {(["codex", "claude"] as AgentProvider[]).map((provider) => (
                        <Button
                          key={provider}
                          type="button"
                          size="sm"
                          variant={provider === "codex" ? "primary" : "outline"}
                          leftIcon={
                            runningAgent === provider ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Terminal className="h-3.5 w-3.5" />
                            )
                          }
                          disabled={runningAgent !== null}
                          onClick={() => void runExecutionAgent(provider)}
                        >
                          {agentLabel[provider]}へ投げる
                        </Button>
                      ))}
                    </div>
                  </div>
                  <pre className="flow-scrollbar mt-2 max-h-72 overflow-y-auto whitespace-pre-wrap rounded-md border border-border bg-bg px-3 py-3 font-mono text-xs leading-relaxed dark:border-border-dark dark:bg-bg-dark">
                    {executionPrompt}
                  </pre>
                </div>
              )}

              {executionPrompt && executionAppendedLogs.length > 0 && (
                <details className="rounded-md border border-border bg-bg dark:border-border-dark dark:bg-bg-dark">
                  <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-muted dark:text-muted-dark">
                    元プロンプト / 追加相談
                  </summary>
                  <div className="border-t border-border px-3 py-3 dark:border-border-dark">
                    <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                      <p className="text-xs font-medium text-muted dark:text-muted-dark">AIへ投げるプロンプト</p>
                      <div className="grid grid-cols-2 gap-2">
                        {(["codex", "claude"] as AgentProvider[]).map((provider) => (
                          <Button
                            key={provider}
                            type="button"
                            size="sm"
                            variant={provider === "codex" ? "primary" : "outline"}
                            leftIcon={
                              runningAgent === provider ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Terminal className="h-3.5 w-3.5" />
                              )
                            }
                            disabled={runningAgent !== null}
                            onClick={() => void runExecutionAgent(provider, withAdditionalConsultation(executionPrompt, followupDraft))}
                          >
                            {agentLabel[provider]}へ再投げ
                          </Button>
                        ))}
                      </div>
                    </div>
                    <div className="mt-3">
                      <p className="text-xs font-medium text-muted dark:text-muted-dark">たいがさんの追加入力</p>
                      <Textarea
                        value={followupDraft}
                        onChange={(e) => setFollowupDraft(e.target.value)}
                        placeholder="例: さっきの回答だとB/Cの境目が曖昧。実装なしで、diff案の粒度だけ具体化して。"
                        className="mt-2 min-h-28 resize-y text-sm leading-relaxed"
                      />
                    </div>
                    <pre className="flow-scrollbar mt-2 max-h-72 overflow-y-auto whitespace-pre-wrap rounded-md border border-border bg-card px-3 py-3 font-mono text-xs leading-relaxed dark:border-border-dark dark:bg-card-dark">
                      {executionPrompt}
                    </pre>
                  </div>
                </details>
              )}

              {(runningAgent || agentError || agentResult) && (
                <div className="rounded-md border border-border bg-bg px-3 py-3 dark:border-border-dark dark:bg-bg-dark">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs font-medium text-muted dark:text-muted-dark">Agent Result</p>
                    {agentResult && (
                      <p className="text-xs text-muted dark:text-muted-dark">
                        {agentLabel[agentResult.provider]} / {Math.round(agentResult.elapsedMs / 1000)}s
                      </p>
                    )}
                  </div>
                  {runningAgent && (
                    <div className="mt-3 flex items-center gap-2 text-sm text-muted dark:text-muted-dark">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {agentLabel[runningAgent]}が相談中...
                    </div>
                  )}
                  {agentError && (
                    <p className="mt-3 whitespace-pre-wrap break-words text-sm text-red-600 dark:text-red-300">
                      {agentError}
                    </p>
                  )}
                  {agentResult && (
                    <>
                      <div className="mt-3 rounded-md border border-border bg-card px-3 py-2 dark:border-border-dark dark:bg-card-dark">
                        <p className="text-xs text-muted dark:text-muted-dark">Safety</p>
                        <p className="mt-1 text-sm text-emerald-700 dark:text-emerald-200">{agentResult.safetyMode}</p>
                      </div>
                      {liveAgentDigest && (
                        <div className="mt-3">
                          <AgentDigestCard digest={liveAgentDigest} />
                        </div>
                      )}
                      <pre className="flow-scrollbar mt-3 max-h-80 overflow-y-auto whitespace-pre-wrap break-words rounded-md border border-border bg-card px-3 py-3 font-sans text-sm leading-relaxed dark:border-border-dark dark:bg-card-dark">
                        {cleanAgentOutput(agentResult.output) || "(no output)"}
                      </pre>
                      <div className="mt-3 flex flex-col gap-2 border-t border-border pt-3 dark:border-border-dark md:flex-row md:items-center md:justify-between">
                        <div className="min-h-5 text-xs text-muted dark:text-muted-dark">
                          {appendState === "saved" && (
                            <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-200">
                              <CheckCircle2 className="h-3.5 w-3.5" />
                              {isEdit ? "Mission本文に追記して保存しました" : "Mission本文に追記しました"}
                            </span>
                          )}
                          {appendState === "error" && (
                            <span className="text-red-600 dark:text-red-300">{appendError ?? "追記に失敗しました"}</span>
                          )}
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="primary"
                          leftIcon={<FileText className="h-3.5 w-3.5" />}
                          onClick={() => void appendAgentResultToMission()}
                        >
                          追記して次の判断へ
                        </Button>
                      </div>
                      {agentResult.stderr && (
                        <div className="mt-3 rounded-md border border-border bg-card dark:border-border-dark dark:bg-card-dark">
                          <button
                            type="button"
                            className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-xs font-semibold text-muted hover:text-text dark:text-muted-dark dark:hover:text-text-dark"
                            onClick={() => setShowDiagnostics((value) => !value)}
                          >
                            Diagnostics
                            <ChevronDown className={`h-3.5 w-3.5 transition ${showDiagnostics ? "rotate-180" : ""}`} />
                          </button>
                          {showDiagnostics && (
                            <pre className="flow-scrollbar max-h-32 overflow-y-auto whitespace-pre-wrap break-words border-t border-border px-3 py-2 font-mono text-xs leading-relaxed text-amber-700 dark:border-border-dark dark:text-amber-200">
                              {agentResult.stderr}
                            </pre>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {executionWatchPoints.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted dark:text-muted-dark">たいがさんが見るポイント</p>
                  <div className="mt-2 grid gap-2 md:grid-cols-3">
                    {executionWatchPoints.map((item) => (
                      <div key={item} className="rounded-md border border-border bg-bg px-3 py-2 dark:border-border-dark dark:bg-bg-dark">
                        {item.replace(/^-\s*/, "")}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {previousAppendedLogs.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted dark:text-muted-dark">過去の追記ログ</p>
                  <div className="mt-2 space-y-2">
                    {previousAppendedLogs.map((log, index) => (
                      <details
                        key={`${log.header}-${index}`}
                        className="rounded-md border border-border bg-bg dark:border-border-dark dark:bg-bg-dark"
                      >
                        <summary className="cursor-pointer px-3 py-2 text-sm font-semibold">
                          Agent Result追記 ({log.header})
                        </summary>
                        <pre className="flow-scrollbar max-h-72 overflow-y-auto whitespace-pre-wrap break-words border-t border-border px-3 py-3 font-sans text-sm leading-relaxed dark:border-border-dark">
                          {log.body}
                        </pre>
                      </details>
                    ))}
                  </div>
                </div>
              )}

              {isEdit && task && executionAppendedLogs.length > 0 && (
                <div className="rounded-md border border-border bg-bg px-3 py-3 dark:border-border-dark dark:bg-bg-dark">
                  <p className="text-xs font-medium text-muted dark:text-muted-dark">Mission締め</p>
                  <div className="mt-2 grid gap-2 md:grid-cols-3">
                    <Button
                      type="button"
                      variant="primary"
                      leftIcon={<CheckCircle2 className="h-3.5 w-3.5" />}
                      onClick={() => void closeMission("complete")}
                    >
                      完了にする
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      leftIcon={<Calendar className="h-3.5 w-3.5" />}
                      onClick={() => void closeMission("tomorrow")}
                    >
                      明日へ送る
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => void closeMission("hold")}
                    >
                      保留にする
                    </Button>
                  </div>
                  <p className="mt-2 text-xs text-muted dark:text-muted-dark">
                    本文を保存してから、ローカルのMission状態だけを更新します。
                  </p>
                </div>
              )}
            </div>
          ) : (
            <Textarea
              placeholder={commandMode ? "指令メモ / なぜ今か" : "メモ"}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="min-h-[34vh] resize-y whitespace-pre-wrap text-sm leading-relaxed md:min-h-[520px]"
            />
          )}
        </div>
        {compactMissionFields ? (
          <details className="rounded-md border border-border bg-card dark:border-border-dark dark:bg-card-dark">
            <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-muted dark:text-muted-dark">
              期日・優先度の詳細設定
            </summary>
            <div className="space-y-4 border-t border-border px-3 py-3 dark:border-border-dark">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-xs font-medium text-muted dark:text-muted-dark"><Calendar className="h-3.5 w-3.5" />期日</div>
                <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
                <div className="flex gap-2">
                  {quickDates.map((d) => <Button key={d.label} type="button" size="sm" variant="outline" onClick={() => setDueDate(d.value)}>{d.label}</Button>)}
                </div>
              </div>
              <RadioGroup value={priority} onChange={setPriority} options={[{ value: "high", label: "高" }, { value: "medium", label: "中" }, { value: "low", label: "低" }]} />
            </div>
          </details>
        ) : (
          <>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs font-medium text-muted dark:text-muted-dark"><Calendar className="h-3.5 w-3.5" />期日</div>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
              <div className="flex gap-2">
                {quickDates.map((d) => <Button key={d.label} type="button" size="sm" variant="outline" onClick={() => setDueDate(d.value)}>{d.label}</Button>)}
              </div>
            </div>
            <RadioGroup value={priority} onChange={setPriority} options={[{ value: "high", label: "高" }, { value: "medium", label: "中" }, { value: "low", label: "低" }]} />
          </>
        )}
      </div>
      <div className="flex flex-col gap-3 border-t border-border px-5 py-4 dark:border-border-dark sm:flex-row sm:items-center sm:justify-between">
        <div className="text-xs leading-relaxed text-muted dark:text-muted-dark">⌘Return 保存　⌘⇧Return 続けて追加　Esc 閉じる</div>
        <div className="grid grid-cols-2 gap-2 sm:flex">
          <Button type="button" variant="ghost" className="w-full whitespace-nowrap sm:w-auto" onClick={onClose}>キャンセル</Button>
          <Button type="submit" variant="primary" className="w-full whitespace-nowrap sm:w-auto">{commandMode ? "Mission保存" : "保存"}</Button>
        </div>
      </div>
    </form>
  );
}
